import React, { useState, useEffect, useRef } from "react";
import Dropdown from "react-bootstrap/Dropdown";
import { Button, Card, CardContent, Typography, Box, Chip, TextField, Tabs, Tab } from "@mui/material";
import { useTranslation } from "react-i18next";
import DownloadIcon from "@mui/icons-material/Download";
import EventIcon from "@mui/icons-material/Event";
import PeopleIcon from "@mui/icons-material/People";
import AccountBalanceWalletIcon from "@mui/icons-material/AccountBalanceWallet";

import styles from "./DownloadCsvPage.module.css";
import { getTranslation } from "../../../utils/translation";

export default function DownloadCsvPage() {
	const { i18n } = useTranslation();
	const [selectedState, setSelectedState] = useState("");
	const [fromDate, setFromDate] = useState("");
	const [toDate, setToDate] = useState("");
	const [isDownloading, setIsDownloading] = useState(false);
	const [events, setEvents] = useState([]);
	const [selectedEvent, setSelectedEvent] = useState(null);
	const [isLoadingEvents, setIsLoadingEvents] = useState(false);
	
	// Dollar point management states
	const [memberId, setMemberId] = useState("");
	const [dollarPointAmount, setDollarPointAmount] = useState("");
	const [isProcessingDollarPoint, setIsProcessingDollarPoint] = useState(false);
	const [activeTab, setActiveTab] = useState(0);
	
	// Deduct dollar point states
	const [deductMemberId, setDeductMemberId] = useState("");
	const [deductAmount, setDeductAmount] = useState("");
	const [isProcessingDeduct, setIsProcessingDeduct] = useState(false);
	
	// Refs for dropdowns
	const stateDropdownRef = useRef(null);
	const eventDropdownRef = useRef(null);

	// Use provided state list with Chinese names
	const [state, setState] = useState([
		{ label: 'ALL', value: 'ALL', labelCN: '全部' },
		{ label: 'ACT', value: 'ACT', labelCN: '首都領地' },
		{ label: 'NSW', value: 'NSW', labelCN: '新南威爾士' },
		{ label: 'NT', value: 'NT', labelCN: '北領地' },
		{ label: 'QLD', value: 'QLD', labelCN: '昆士蘭' },
		{ label: 'SA', value: 'SA', labelCN: '南澳大利亞' },
		{ label: 'TAS', value: 'TAS', labelCN: '塔斯馬尼亞' },
		{ label: 'VIC', value: 'VIC', labelCN: '維多利亞' },
		{ label: 'WA', value: 'WA', labelCN: '西澳大利亞' },
	]);

	const handleStateSelect = (stateValue) => {
		setSelectedState(stateValue);
		setSelectedEvent(null); // Reset selected event when state changes
		setEvents([]); // Clear events when state changes
	};

	const handleEventSelect = (eventId) => {
		
		if (!eventId) {
			setSelectedEvent(null);
			return;
		}
		
		// Convert eventId to number for comparison since select values are strings
		const numericEventId = parseInt(eventId, 10);
		
		// Try different possible event ID field names with type conversion
		let event = events.find(e => e.eventId === numericEventId);
		if (!event) {
			event = events.find(e => e.id === numericEventId);
		}
		if (!event) {
			event = events.find(e => e.event_id === numericEventId);
		}
		if (!event) {
			event = events.find(e => e._id === numericEventId);
		}
		
		// Also try string comparison as fallback
		if (!event) {
			event = events.find(e => e.eventId == eventId); // Use == for type coercion
		}
		if (!event) {
			event = events.find(e => e.id == eventId);
		}
		if (!event) {
			event = events.find(e => e.event_id == eventId);
		}
		if (!event) {
			event = events.find(e => e._id == eventId);
		}
		
		if (event) {
			setSelectedEvent(event);
		} else {
			console.error("Event not found for ID:", eventId);
			setSelectedEvent(null);
		}
	};

	const handleDateChange = (field, value) => {
		if (field === 'fromDate') {
			setFromDate(value);
		} else if (field === 'toDate') {
			setToDate(value);
		}
		setSelectedEvent(null); // Reset selected event when dates change
		setEvents([]); // Clear events when dates change
	};

	const fetchEvents = async () => {
		if (!selectedState || !fromDate || !toDate) return;
		
		setIsLoadingEvents(true);
		try {
			const url = `https://x4rau8a9vk.execute-api.ap-southeast-2.amazonaws.com/prod/events?state=${selectedState}&fromDate=${fromDate}&toDate=${toDate}`;
			const response = await fetch(url);
			const data = await response.json();
			
			setEvents(data.data || []);
		} catch (error) {
			console.error('Error fetching events:', error);
			setEvents([]);
		} finally {
			setIsLoadingEvents(false);
		}
	};

	const handleDownload = async (type) => {
		
		if (type === 'events' && !selectedEvent) {
			alert('Please select an event first');
			return;
		}

		setIsDownloading(true);
		try {
			if (type === 'events' && selectedEvent) {
				// Get the event ID from various possible fields
				const eventId = selectedEvent.eventId || selectedEvent.id || selectedEvent.event_id || selectedEvent._id;
				
				// Download CSV for specific event
				const url = `https://x4rau8a9vk.execute-api.ap-southeast-2.amazonaws.com/prod/admin/report/event/${eventId}`;
				
				const response = await fetch(url, {
					method: 'GET',
					headers: {
						'Accept': 'text/csv'
					}
				});
				
				if (!response.ok) {
					throw new Error(`HTTP error! status: ${response.status}`);
				}
				
				const blob = await response.blob();
				
				const url2 = window.URL.createObjectURL(blob);
				const a = document.createElement('a');
				a.href = url2;
				a.download = `event_${eventId}_${fromDate}_${toDate}.csv`;
				document.body.appendChild(a);
				a.click();
				window.URL.revokeObjectURL(url2);
				document.body.removeChild(a);
				
			} else if (type === 'members') {
				// Download members CSV using the correct API endpoint
				const url = `https://x4rau8a9vk.execute-api.ap-southeast-2.amazonaws.com/prod/admin/report/members`;
				
				const response = await fetch(url, {
					method: 'GET',
					headers: {
						'Accept': 'text/csv'
					}
				});
				
				if (!response.ok) {
					throw new Error(`HTTP error! status: ${response.status}`);
				}
				
				const blob = await response.blob();
				
				const url2 = window.URL.createObjectURL(blob);
				const a = document.createElement('a');
				a.href = url2;
				a.download = `members_${new Date().toISOString().split('T')[0]}.csv`;
				document.body.appendChild(a);
				a.click();
				window.URL.revokeObjectURL(url2);
				document.body.removeChild(a);
				
			}
		} catch (error) {
			console.error('Error downloading:', error);
			alert('Download failed. Please try again.');
		} finally {
			setIsDownloading(false);
		}
	};

	const handleDeductDollarPoint = async () => {
		if (!deductMemberId || !deductAmount) {
			alert('Please enter both Member ID and Deduct Amount');
			return;
		}

		const amount = parseFloat(deductAmount);
		if (isNaN(amount) || amount <= 0) {
			alert('Please enter a valid positive number for Deduct Amount');
			return;
		}

		setIsProcessingDeduct(true);
		try {
			const accessToken = localStorage.getItem("adminAccessToken");
			if (!accessToken) {
				throw new Error("No access token found. Please login again.");
			}

			const url = `https://x4rau8a9vk.execute-api.ap-southeast-2.amazonaws.com/prod/admin/members/dollar-point/withdraw`;
			const response = await fetch(url, {
				method: 'POST',
				headers: {
					'Content-Type': 'application/json',
					'Authorization': `Bearer ${accessToken}`,
				},
				body: JSON.stringify({
					userId: deductMemberId,
					withdrawAmount: amount
				})
			});

			if (!response.ok) {
				const errorData = await response.json().catch(() => ({}));
				throw new Error(errorData.message || `HTTP error! status: ${response.status}`);
			}

			const result = await response.json();
			
			alert(`Successfully deducted ${amount} dollar points from member ${deductMemberId}`);
			
			// Clear the form
			setDeductMemberId("");
			setDeductAmount("");
			
		} catch (error) {
			console.error('Error deducting dollar point:', error);
			alert(`Failed to deduct dollar point: ${error.message}`);
		} finally {
			setIsProcessingDeduct(false);
		}
	};

	const handleDollarPointUpdate = async () => {
		if (!memberId || !dollarPointAmount) {
			alert('Please enter both Member ID and Dollar Point amount');
			return;
		}

		const amount = parseFloat(dollarPointAmount);
		if (isNaN(amount) || amount < 0) {
			alert('Please enter a valid non-negative number for Dollar Point amount');
			return;
		}

		setIsProcessingDollarPoint(true);
		try {
			const accessToken = localStorage.getItem("adminAccessToken");
			if (!accessToken) {
				throw new Error("No access token found. Please login again.");
			}

			const url = `https://x4rau8a9vk.execute-api.ap-southeast-2.amazonaws.com/prod/admin/member/dollar-point`;
			const response = await fetch(url, {
				method: 'PATCH',
				headers: {
					'Content-Type': 'application/json',
					'Authorization': `Bearer ${accessToken}`,
				},
				body: JSON.stringify({
					userId: memberId,
					dollarPoint: amount
				})
			});

			if (!response.ok) {
				const errorData = await response.json().catch(() => ({}));
				throw new Error(errorData.message || `HTTP error! status: ${response.status}`);
			}

			const result = await response.json();
			
			alert(`Successfully updated dollar point to ${amount} for member ${memberId}`);
			
			// Clear the form
			setMemberId("");
			setDollarPointAmount("");
			
		} catch (error) {
			console.error('Error updating dollar point:', error);
			alert(`Failed to update dollar point: ${error.message}`);
		} finally {
			setIsProcessingDollarPoint(false);
		}
	};

	// Get language-specific title and description
	const getLocalizedContent = (event) => {
		const currentLang = i18n.language;
		let title, description;
		
		// Try different possible field names for title
		const titleEN = event.titleEN || event.title || event.title_en || event.name;
		const titleTC = event.titleTC || event.title_tc || event.titleHK || event.title_hk;
		const titleSC = event.titleSC || event.title_sc || event.titleCN || event.title_cn;
		
		// Try different possible field names for description
		const descriptionEN = event.descriptionEN || event.description || event.description_en || event.desc || event.desc_en;
		const descriptionTC = event.descriptionTC || event.description_tc || event.descriptionHK || event.description_hk || event.desc_tc || event.desc_hk;
		const descriptionSC = event.descriptionSC || event.description_sc || event.descriptionCN || event.description_cn || event.desc_sc || event.desc_cn;
		
		if (currentLang === 'zh-hk') {
			title = titleTC || titleEN;
			description = descriptionTC || descriptionEN;
		} else if (currentLang === 'zh-cn') {
			title = titleSC || titleEN;
			description = descriptionSC || descriptionEN;
		} else {
			title = titleEN;
			description = descriptionEN;
		}
		
		// Fallback values if still undefined
		title = title || 'No Title Available';
		description = description || 'No Description Available';
		
		return { title, description };
	};

	// Get localized state label
	const getLocalizedStateLabel = (stateItem) => {
		const currentLang = i18n.language;
		if (currentLang === 'zh-cn' || currentLang === 'zh-hk') {
			return `${stateItem.label} - ${stateItem.labelCN}`;
		}
		return stateItem.label;
	};

	// Auto-fetch events when state or dates change
	useEffect(() => {
		if (selectedState && fromDate && toDate) {
			fetchEvents();
		}
	}, [selectedState, fromDate, toDate]);

	return (
		<div className={styles["download-page-container"]}>
			{/* Page Header */}
			<Box className={styles["page-header"]}>
				<Typography variant="h4" className={styles["page-title"]}>
					{getTranslation(i18n, "downloadCsvPage.title")}
				</Typography>
				<Typography variant="body1" className={styles["page-subtitle"]}>
					{getTranslation(i18n, "downloadCsvPage.description.exportAndDownload")}
				</Typography>
			</Box>

			{/* Download Cards */}
			<Box className={styles["cards-container"]}>
				{/* Main Admin Panel Card with Tabs */}
				<Card className={`${styles["download-card"]} ${styles["card-1"]}`}>
					<CardContent className={styles["card-content"]}>
						<Box className={styles["card-header"]}>
							<EventIcon className={styles["card-icon"]} />
							<Typography variant="h5" className={styles["card-title"]}>
								{getTranslation(i18n, "adminDrawer.title")}
							</Typography>
						</Box>
						
						{/* Tabs */}
						<Box className={styles["tabs-container"]}>
							<Tabs 
								value={activeTab} 
								onChange={(e, newValue) => setActiveTab(newValue)}
								className={styles["admin-tabs"]}
								variant="fullWidth"
							>
								<Tab 
									label={getTranslation(i18n, "downloadCsvPage.tabs.downloadCsvFiles")}
									className={styles["tab-item"]}
									value={0}
								/>
								<Tab 
									label={getTranslation(i18n, "downloadCsvPage.tabs.updateDollarPoint")}
									className={styles["tab-item"]}
									value={1}
								/>
								<Tab 
									label={getTranslation(i18n, "downloadCsvPage.tabs.deductDollarPoint")}
									className={styles["tab-item"]}
									value={2}
								/>
							</Tabs>
						</Box>

						{/* Tab Content */}
						{activeTab === 0 && (
							<Box className={styles["tab-content"]}>
								<Typography variant="body2" className={styles["card-description"]}>
									{getTranslation(i18n, "downloadCsvPage.description.downloadEvents")}
								</Typography>

								{/* Date Range Selection */}
								<Box className={styles["date-range-section"]}>
									<Typography variant="subtitle2" className={styles["selection-label"]}>
										{getTranslation(i18n, "downloadCsvPage.form.dateRange")}
									</Typography>
									<Box className={styles["date-inputs"]}>
										<TextField
											type="date"
											label={getTranslation(i18n, "downloadCsvPage.form.fromDate")}
											value={fromDate}
											onChange={(e) => handleDateChange('fromDate', e.target.value)}
											InputLabelProps={{ shrink: true }}
											className={styles["date-input"]}
										/>
										<TextField
											type="date"
											label={getTranslation(i18n, "downloadCsvPage.form.toDate")}
											value={toDate}
											onChange={(e) => handleDateChange('toDate', e.target.value)}
											InputLabelProps={{ shrink: true }}
											className={styles["date-input"]}
										/>
									</Box>
								</Box>

								{/* State Selection */}
								<Box className={styles["selection-area"]}>
									<Typography variant="subtitle2" className={styles["selection-label"]}>
										{getTranslation(i18n, "downloadCsvPage.form.step1SelectState")}
									</Typography>
									<select
										value={selectedState}
										onChange={(e) => handleStateSelect(e.target.value)}
										className={styles["native-select"]}
									>
										<option value="">{getTranslation(i18n, "downloadCsvPage.form.selectState")}</option>
										{state.map((entry) => (
											<option key={entry.value} value={entry.value}>
												{getLocalizedStateLabel(entry)}
											</option>
										))}
									</select>
								</Box>

								{/* Event Selection - Only show when events are available */}
								{events.length > 0 && (
									<Box className={styles["selection-area"]}>
										<Typography variant="subtitle2" className={styles["selection-label"]}>
											{getTranslation(i18n, "downloadCsvPage.form.step2SelectEvent")}
										</Typography>
										<select
											value={selectedEvent ? (selectedEvent.eventId || selectedEvent.id || selectedEvent.event_id || selectedEvent._id) : ""}
											onChange={(e) => handleEventSelect(e.target.value)}
											className={styles["native-select"]}
										>
											<option value="">{getTranslation(i18n, "downloadCsvPage.form.selectEvent")}</option>
											{events.map((event) => {
												const { description } = getLocalizedContent(event);
												
												// Get the event ID from various possible fields
												const eventId = event.eventId || event.id || event.event_id || event._id;
												
												// Format date and time
												const eventDate = event.eventDate ? new Date(event.eventDate).toLocaleDateString() : '';
												const startTime = event.startTime ? event.startTime.substring(0, 5) : ''; // Remove seconds
												const endTime = event.endTime ? event.endTime.substring(0, 5) : ''; // Remove seconds
												const venue = event.venue || '';
												
												// Create display text
												let displayText = '';
												if (eventDate) displayText += `${eventDate} `;
												if (startTime && endTime) displayText += `${startTime}-${endTime} `;
												else if (startTime) displayText += `${startTime} `;
												
												if (venue) displayText += `@ ${venue} `;
												
												// Add description (truncated if too long)
												if (description) {
													const shortDesc = description.length > 50 ? description.substring(0, 50) + '...' : description;
													displayText += `- ${shortDesc}`;
												}
												
												return (
													<option key={eventId} value={eventId}>
														{displayText.trim()}
													</option>
												);
											})}
										</select>
										
										{/* Show selected event details */}
										{selectedEvent && (
											<Box className={styles["selected-event-details"]}>
												<Typography variant="body2" className={styles["event-detail-title"]}>
													<strong>{getTranslation(i18n, "downloadCsvPage.form.selectedEvent")}:</strong>
												</Typography>
												<Box className={styles["event-detail-content"]}>
													{selectedEvent.eventDate && (
														<Typography variant="body2" className={styles["event-detail-item"]}>
															<strong>{getTranslation(i18n, "downloadCsvPage.form.date")}:</strong> {new Date(selectedEvent.eventDate).toLocaleDateString()}
														</Typography>
													)}
													{(selectedEvent.startTime || selectedEvent.endTime) && (
														<Typography variant="body2" className={styles["event-detail-item"]}>
															<strong>{getTranslation(i18n, "downloadCsvPage.form.time")}:</strong> {selectedEvent.startTime ? selectedEvent.startTime.substring(0, 5) : ''} {selectedEvent.endTime ? `- ${selectedEvent.endTime.substring(0, 5)}` : ''}
														</Typography>
													)}
													{selectedEvent.venue && (
														<Typography variant="body2" className={styles["event-detail-item"]}>
															<strong>{getTranslation(i18n, "downloadCsvPage.form.venue")}:</strong> {selectedEvent.venue}
														</Typography>
													)}
													<Typography variant="body2" className={styles["event-detail-item"]}>
														<strong>{getTranslation(i18n, "downloadCsvPage.form.title")}:</strong> {getLocalizedContent(selectedEvent).title}
													</Typography>
													<Typography variant="body2" className={styles["event-detail-item"]}>
														<strong>{getTranslation(i18n, "downloadCsvPage.form.description")}:</strong> {getLocalizedContent(selectedEvent).description}
													</Typography>
												</Box>
											</Box>
										)}
									</Box>
								)}

								{/* Loading State */}
								{isLoadingEvents && (
									<Box className={styles["loading-state"]}>
										<Typography variant="body2">{getTranslation(i18n, "downloadCsvPage.form.loadingEvents")}</Typography>
									</Box>
								)}

								{/* Download Button */}
								<Box className={styles["controls-group"]}>
									<Button 
										variant="contained" 
										startIcon={<DownloadIcon />}
										className={`${styles["download-button"]} ${styles["events-button"]}`}
										onClick={() => handleDownload('events')}
										disabled={isDownloading || !selectedEvent}
									>
										{isDownloading ? getTranslation(i18n, "downloadCsvPage.form.downloading") : getTranslation(i18n, "downloadCsvPage.downloadEvents.button")}
									</Button>
								</Box>
							</Box>
						)}

													{/* Update Dollar Point Tab */}
							{activeTab === 1 && (
								<Box className={styles["tab-content"]}>
									<Typography variant="body2" className={styles["card-description"]}>
										{getTranslation(i18n, "downloadCsvPage.description.updateDollarPoint")}
									</Typography>

									<Box className={styles["dollar-point-section"]}>
										<Typography variant="subtitle2" className={styles["section-title"]}>
											{getTranslation(i18n, "downloadCsvPage.dollarPointManagement.title")}
										</Typography>
										
										<Box className={styles["input-group"]}>
											<TextField
												label={getTranslation(i18n, "downloadCsvPage.dollarPointManagement.memberId")}
												value={memberId}
												onChange={(e) => setMemberId(e.target.value)}
												placeholder={getTranslation(i18n, "downloadCsvPage.dollarPointManagement.memberIdPlaceholder")}
												className={styles["input-field"]}
												fullWidth
											/>
										</Box>
										
										<Box className={styles["input-group"]}>
											<TextField
												label={getTranslation(i18n, "downloadCsvPage.dollarPointManagement.amount")}
												value={dollarPointAmount}
												onChange={(e) => setDollarPointAmount(e.target.value)}
												placeholder={getTranslation(i18n, "downloadCsvPage.dollarPointManagement.amountPlaceholder")}
												type="number"
												className={styles["input-field"]}
												fullWidth
											/>
										</Box>
										
										<Box className={styles["controls-group"]}>
											<Button 
												variant="contained" 
												color="primary"
												startIcon={<AccountBalanceWalletIcon />}
												className={styles["update-button"]}
												onClick={handleDollarPointUpdate}
												disabled={isProcessingDollarPoint || !memberId || !dollarPointAmount}
											>
												{isProcessingDollarPoint ? getTranslation(i18n, "downloadCsvPage.dollarPointManagement.processing") : getTranslation(i18n, "downloadCsvPage.dollarPointManagement.button")}
											</Button>
										</Box>
									</Box>
								</Box>
							)}

							{/* Deduct Dollar Point Tab */}
							{activeTab === 2 && (
								<Box className={styles["tab-content"]}>
									<Typography variant="body2" className={styles["card-description"]}>
										{getTranslation(i18n, "downloadCsvPage.description.deductDollarPoint")}
									</Typography>

									<Box className={styles["dollar-point-section"]}>
										<Typography variant="subtitle2" className={styles["section-title"]}>
											{getTranslation(i18n, "downloadCsvPage.deductDollarPointManagement.title")}
										</Typography>
										
										<Box className={styles["input-group"]}>
											<TextField
												label={getTranslation(i18n, "downloadCsvPage.deductDollarPointManagement.memberId")}
												value={deductMemberId}
												onChange={(e) => setDeductMemberId(e.target.value)}
												placeholder={getTranslation(i18n, "downloadCsvPage.deductDollarPointManagement.memberIdPlaceholder")}
												className={styles["input-field"]}
												fullWidth
											/>
										</Box>
										
										<Box className={styles["input-group"]}>
											<TextField
												label={getTranslation(i18n, "downloadCsvPage.deductDollarPointManagement.amount")}
												value={deductAmount}
												onChange={(e) => setDeductAmount(e.target.value)}
												placeholder={getTranslation(i18n, "downloadCsvPage.deductDollarPointManagement.amountPlaceholder")}
												type="number"
												className={styles["input-field"]}
												fullWidth
											/>
										</Box>
										
										<Box className={styles["controls-group"]}>
											<Button 
												variant="contained" 
												color="warning"
												startIcon={<AccountBalanceWalletIcon />}
												className={styles["deduct-button"]}
												onClick={handleDeductDollarPoint}
												disabled={isProcessingDeduct || !deductMemberId || !deductAmount}
											>
												{isProcessingDeduct ? getTranslation(i18n, "downloadCsvPage.deductDollarPointManagement.processing") : getTranslation(i18n, "downloadCsvPage.deductDollarPointManagement.button")}
											</Button>
										</Box>
									</Box>
								</Box>
							)}
					</CardContent>
				</Card>

				{/* Members Download Card */}
				<Card className={`${styles["download-card"]} ${styles["card-2"]}`}>
					<CardContent className={styles["card-content"]}>
						<Box className={styles["card-header"]}>
							<PeopleIcon className={styles["card-icon"]} />
							<Typography variant="h5" className={styles["card-title"]}>
								{getTranslation(i18n, "downloadCsvPage.downloadMembers.subtitle")}
							</Typography>
						</Box>
						
						<Typography variant="body2" className={styles["card-description"]}>
							{getTranslation(i18n, "downloadCsvPage.description.downloadMembers")}
						</Typography>

						<Box className={styles["controls-group-simple"]}>
							<Button 
								variant="contained" 
								startIcon={<DownloadIcon />}
								className={`${styles["download-button"]} ${styles["members-button"]}`}
								onClick={() => handleDownload('members')}
								disabled={isDownloading}
							>
								{isDownloading ? getTranslation(i18n, "downloadCsvPage.form.downloading") : getTranslation(i18n, "downloadCsvPage.downloadMembers.button")}
							</Button>
						</Box>
					</CardContent>
				</Card>
			</Box>

			{/* Statistics Section */}
			<Box className={styles["stats-section"]}>
				<Typography variant="h6" className={styles["stats-title"]}>
					{getTranslation(i18n, "downloadCsvPage.stats.quickStats")}
				</Typography>
				<Box className={styles["stats-grid"]}>
					{/* <Box className={styles["stat-item"]}>
						<Typography variant="h4" className={styles["stat-number"]}>{events.length}</Typography>
						<Typography variant="body2" className={styles["stat-label"]}>Available Events</Typography>
					</Box>
					<Box className={styles["stat-item"]}>
						<Typography variant="h4" className={styles["stat-number"]}>5,678</Typography>
						<Typography variant="body2" className={styles["stat-label"]}>Active Members</Typography>
					</Box> */}
					<Box className={styles["stat-item"]}>
						<Typography variant="h4" className={styles["stat-number"]}>{state.length}</Typography>
						<Typography variant="body2" className={styles["stat-label"]}>{getTranslation(i18n, "downloadCsvPage.stats.states")}</Typography>
					</Box>
				</Box>
			</Box>
		</div>
	);
}
